// JSON-LD schema builders for the StoicOs site.
// All builders return plain objects that useSEO() will JSON.stringify into the head.

const SITE = "https://stoicos.ai";

export const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url.startsWith("http") ? it.url : `${SITE}${it.url}`,
  })),
});

export const faqPage = (qa) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map((row) => ({
    "@type": "Question",
    name: row.q,
    acceptedAnswer: { "@type": "Answer", text: row.a },
  })),
});

export const sage = ({ name, alternateName, role, birthDate, deathDate, image, description, worksKnownFor }) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name,
  alternateName,
  jobTitle: role,
  birthDate,
  deathDate,
  image,
  description,
  knowsAbout: ["Stoicism", "Philosophy", "Ethics", "Practical Wisdom", "Self-Knowledge"],
  ...(worksKnownFor ? { sameAs: worksKnownFor } : {}),
});

export const webPage = ({ name, description, url, breadcrumbItems }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description,
  url: url.startsWith("http") ? url : `${SITE}${url}`,
  isPartOf: { "@type": "WebSite", name: "StoicOs", url: SITE },
  ...(breadcrumbItems ? { breadcrumb: breadcrumb(breadcrumbItems) } : {}),
});

/**
 * Build a schema.org `Article` node.
 *
 * @param {Object}  params
 * @param {string}  params.inLanguage       BCP-47 tag of the active locale ("en" | "nl"). Optional.
 * @param {Object=} params.translation      When another locale exists, describe it here.
 *                                          Shape: `{ inLanguage, headline, url? }`. If provided,
 *                                          the returned Article gains either `workTranslation`
 *                                          (when the active article is the source) or
 *                                          `translationOfWork` (when the active article is the
 *                                          translation). The direction is decided by `translation.role`
 *                                          which must be `"translation"` (this article's counterpart is
 *                                          the translation) or `"source"` (this article IS the
 *                                          translation of the counterpart).
 *
 * The counterpart is always emitted as a nested `Article` node with a
 * distinct `@id` (`{url}#<lang>`) so a single JSON-LD graph does not
 * declare two Articles under the same identifier.
 */
export const article = ({
  headline,
  description,
  url,
  image,
  datePublished,
  dateModified,
  authorName,
  authorRole,
  articleSection,
  inLanguage,
  translation,
}) => {
  const abs = (u) => (u.startsWith("http") ? u : `${SITE}${u}`);
  const canonical = abs(url);

  const node = {
    "@context": "https://schema.org",
    "@type": "Article",
    ...(inLanguage ? { "@id": `${canonical}#${inLanguage}`, inLanguage } : {}),
    headline,
    description,
    image: image ? (image.startsWith("http") ? image : `${SITE}${image}`) : undefined,
    datePublished,
    dateModified: dateModified || datePublished,
    author: { "@type": "Person", name: authorName, ...(authorRole ? { jobTitle: authorRole } : {}) },
    publisher: {
      "@type": "Organization",
      name: "StoicOs",
      url: SITE,
      logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    ...(articleSection ? { articleSection } : {}),
  };

  if (
    translation &&
    typeof translation === "object" &&
    translation.inLanguage &&
    translation.headline &&
    (translation.role === "translation" || translation.role === "source")
  ) {
    const counterpart = {
      "@type": "Article",
      "@id": `${canonical}#${translation.inLanguage}`,
      inLanguage: translation.inLanguage,
      headline: translation.headline,
      url: canonical,
    };
    if (translation.role === "translation") {
      node.workTranslation = counterpart;
    } else {
      node.translationOfWork = counterpart;
    }
  }

  return node;
};

export const SITE_URL = SITE;

/**
 * ItemList schema for an article's Table of Contents.
 *
 * @param {Object}   params
 * @param {string}   params.name     Human-readable list name (e.g. "Table of contents").
 * @param {string}   params.url      Absolute-or-relative URL of the article itself.
 * @param {Array<{ text: string, id: string, level?: number }>} params.items
 *                                   Heading text + auto-generated slug id (matches rehype-slug).
 *
 * Each list item is emitted as `{ position, url: canonical#id, name: cleanText }`.
 * Duplicate ids are dropped (first occurrence wins) so Google doesn't see a
 * ToC pointing to the same anchor twice.
 */
export const itemList = ({ name, url, items }) => {
  const base = url.startsWith("http") ? url : `${SITE}${url}`;
  const seen = new Set();
  const entries = [];
  for (const it of items || []) {
    if (!it || !it.id || !it.text) continue;
    if (seen.has(it.id)) continue;
    seen.add(it.id);
    entries.push({
      "@type": "ListItem",
      position: entries.length + 1,
      name: it.text,
      url: `${base}#${it.id}`,
    });
  }
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: name || "Table of contents",
    itemListElement: entries,
  };
};
